import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { CompanyRegistrationForm } from '../components/company/CompanyRegistrationForm';
import { CompanyStatusCard } from '../components/company/CompanyStatusCard';
import { Header } from '../components/common/Header';
import { Company, CompanyRegistrationRequest } from '../types/auth';
import { toast } from 'react-hot-toast';
import apiService from '../services/api';

export const CompanyPage: React.FC = () => {
    const { user } = useAuth();
    const [company, setCompany] = useState<Company | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [showWizard, setShowWizard] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        loadCompany();
    }, [user?.companyId]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadCompany = async () => {
        if (!user?.companyId) {
            setIsLoading(false);
            return;
        }

        try {
            const response = await apiService.getMyCompany();
            if (response.data.success && response.data.data) {
                setCompany(response.data.data);
            }
        } catch (error: any) {
            console.error('Error loading company:', error);
            if (error.response?.status !== 404) {
                toast.error('Ошибка загрузки данных компании');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleCompanySubmit = async (data: CompanyRegistrationRequest) => {
        setIsSubmitting(true);
        try {
            let response;
            if (company) {
                // Обновляем существующую компанию
                response = await apiService.updateCompany(company.id!, data);
            } else {
                // Создаем новую компанию
                response = await apiService.registerCompany(data);
            }

            if (response.data.success && response.data.data) {
                setCompany(response.data.data);
                toast.success(company ? 'Данные компании обновлены!' : 'Заявка на регистрацию компании отправлена!');
                setShowWizard(false);
            }
        } catch (error: any) {
            console.error('Error with company:', error);
            const message = error.response?.data?.message || 'Ошибка при работе с данными компании';
            toast.error(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEditCompany = () => {
        setShowWizard(true);
    };

    const handleResubmitCompany = () => {
        setShowWizard(true);
    };

    const handleCancelWizard = () => {
        setShowWizard(false);
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Загрузка данных компании...</p>
                </div>
            </div>
        );
    }

    if (showWizard) {
        return (
            <CompanyRegistrationForm
                onSubmit={handleCompanySubmit}
                onCancel={handleCancelWizard}
                isSubmitting={isSubmitting}
            />
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <Header
                showBackButton={true}
                backButtonText="На главную"
                backButtonPath="/client/dashboard"
                title="Компания"
            />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                {company ? (
                    <CompanyStatusCard
                        company={company}
                        onEdit={handleEditCompany}
                        onResubmit={handleResubmitCompany}
                    />
                ) : (
                    <div className="text-center py-12">
                        <div className="w-24 h-24 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <svg className="w-12 h-12 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-2M7 21H5m2 0h2m6-12h2m-2 4h2m-2 4h2M9 7h2m-2 4h2m-2 4h2" />
                            </svg>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">
                            Зарегистрируйте свою компанию
                        </h2>
                        <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
                            Для работы с системой необходимо зарегистрировать компанию.
                            Процесс регистрации включает несколько простых шагов и занимает всего несколько минут.
                        </p>

                        {/* Benefits */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 max-w-4xl mx-auto">
                            <div className="text-center p-6">
                                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                    Быстрая регистрация
                                </h3>
                                <p className="text-gray-600 text-sm">
                                    Пошаговый процесс регистрации занимает всего 5-10 минут
                                </p>
                            </div>

                            <div className="text-center p-6">
                                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                    Безопасность данных
                                </h3>
                                <p className="text-gray-600 text-sm">
                                    Все данные компании защищены и используются только для работы системы
                                </p>
                            </div>

                            <div className="text-center p-6">
                                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                                    <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                    Быстрое одобрение
                                </h3>
                                <p className="text-gray-600 text-sm">
                                    Модерация заявки занимает 1-2 рабочих дня
                                </p>
                            </div>
                        </div>

                        <button
                            onClick={() => setShowWizard(true)}
                            className="btn-primary text-lg px-8 py-3"
                        >
                            Начать регистрацию
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
